// @flow
import React, { memo } from 'react';
import { H6 } from '@finect/tabular-core/Headings';
import { AdviserBadge } from '@finect/tabular-components/AdviserBadge';
import { Tag } from '@finect/tabular-components/Tags';
import { Icon } from '@finect/tabular-components/Icon';
import { OrganizationBadge } from '@finect/tabular-components/Advisers';

import type { NewProfessionalType } from '../../../types';

import { CardBlock, EAGER_LIST_ICONS } from '../../../components';
import {
  ProfessionalCardRow,
  Identity,
  IdentityText,
  NameRow,
  Tagline,
  Company,
  CompanyText,
  ActivityLine,
  Meta,
  Field,
  FieldLabel,
  Chips,
  MonthBadge,
  CertBadges
} from '../partials';

// Etiqueta del certificado: mismo criterio que la ficha de perfil (prefijo "Certificado"
// salvo que el título ya lo lleve).
const buildCertLabel = (title: string): string => {
  const raw = title || '';
  if (!raw) return '';
  return raw.startsWith('Certificado') ? raw : `Certificado ${raw}`;
};

type Props = NewProfessionalType & { index?: number };

const ProfessionalCardComponent = (props: Props) => {
  const {
    displayName,
    icon,
    tagline,
    certifications = [],
    organizations = [],
    specializations = [],
    stats = {},
    isHighlighted,
    index = 0
  } = props;
  const [organization = {}] = organizations;
  const [role] = organization.badges || [];

  const adviserAlt = displayName || 'Asesor';
  const organizationName = (organization && (organization.name || organization.displayName)) || '';
  const hasActivity = Boolean(stats.questions || stats.reviews);
  const hasMeta = Boolean(specializations.length || certifications.length);

  return (
    <CardBlock>
      <ProfessionalCardRow as="div" $highlighted={isHighlighted}>
        <Identity>
          <Icon alt={adviserAlt} image={icon} lazyLoad={index >= EAGER_LIST_ICONS} />
          <IdentityText>
            {isHighlighted ? <MonthBadge>Asesor del mes</MonthBadge> : null}
            <NameRow>
              {/* h3 por jerarquía (cuelga del h2 del listado); el estilo sigue siendo el de H6. */}
              <H6 as="h3">{displayName}</H6>
              {role ? <AdviserBadge title={role} /> : null}
            </NameRow>
            {tagline ? <Tagline>{tagline}</Tagline> : null}
            {organizationName ? (
              <Company>
                <FieldLabel>Empresa</FieldLabel>
                <CompanyText>{organizationName}</CompanyText>
              </Company>
            ) : null}
            {hasActivity ? (
              <ActivityLine>
                {stats.questions ? <span><b>{stats.questions}</b> respuestas</span> : null}
                {stats.reviews ? <span><b>{stats.reviews}</b> reseñas</span> : null}
              </ActivityLine>
            ) : null}
          </IdentityText>
        </Identity>

        {hasMeta ? (
          <Meta>
            {specializations.length ? (
              <Field>
                <FieldLabel>Especializado en</FieldLabel>
                <Chips>
                  {specializations.map((item, ii) => (
                    <Tag small color="positiveD" key={ii}>
                      {item.name}
                    </Tag>
                  ))}
                </Chips>
              </Field>
            ) : null}
            {certifications.length ? (
              <Field>
                <FieldLabel>Certificaciones</FieldLabel>
                <CertBadges>
                  {certifications.map((cert, ii) => (
                    <OrganizationBadge
                      key={cert.id || ii}
                      type="certificate"
                      organization={{ ...cert, displayName: buildCertLabel(cert.title) }}
                      large
                    />
                  ))}
                </CertBadges>
              </Field>
            ) : null}
          </Meta>
        ) : null}
      </ProfessionalCardRow>
    </CardBlock>
  );
};

// El scroll infinito va acumulando tarjetas: sin memo, cada tanda vuelve a
// renderizar todas las anteriores.
export const ProfessionalCard: React$AbstractComponent<Props, mixed> = memo(ProfessionalCardComponent);
